import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid,
  GridCellEditStopReasons,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { apiService } from '../../api/axiosClient';
import { useEffect, useState } from 'react';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import GiftshopUseToolbar from './GiftshopUseToolbar';


const TableGiftUse = ({setGiftID}) => {
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(0)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('desc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('id')
  const [selected, setSelected] = useState([])

  const loadGiftUse = 
    async (sort, query, column) => {
      try {
        const response = await apiService.get('/giftshopuse', {
          params: {
            query,
            sort,
            column,
            page,
            pageSize
          }
        })
        const payload = response.data;
        setTotal(payload.size)
        setList(payload.list)
        setLoading(false)
      } catch (e) {
        console.error(e)
      }
    }

  useEffect(() => {
    loadGiftUse(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadGiftUse(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('id')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadGiftUse(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      flex: 0.1,
      minWidth: 40,
      field: 'id',
      hideable: false,
      headerName: 'ID',
      renderCell: params => ( 
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.id}
        </Typography>
      )
    },
    {
      flex: 0.1,
      minWidth: 50,
      field: 'userid',
      headerName: '고객ID',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.userid}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'gift_id',
      headerName: '상품권',
      renderCell: params => (        
        <a href='#' onClick={() => setGiftID(params.row.gift_id)} >
          <CustomChip
            size='small'
            skin='light'
            color='primary'
            label={params.row.title ? params.row.title : '무효상품권'}
            sx={{ '& .MuiChip-label': { textTransform: 'capitalize' }, padding: 2, cursor: 'pointer' }}
          />
        </a>
      )
    },
    {
      flex: 0.1,
      field: 'recv',
      minWidth: 80,
      headerName: '수령인',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.recv}
        </Typography>
      )
    },
    {
      flex: 0.1,
      field: 'phone',
      minWidth: 80,
      headerName: '연락처',
      editable: true,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.phone}
        </Typography>
      )
    },
    {
      flex: 0.1,
      field: 'qty',
      minWidth: 80,
      headerName: '수량',
      editable: true,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.qty}
        </Typography>
      )
    },
    {
      flex: 0.1,
      field: 'use_point',
      minWidth: 80,
      headerName: '사용포인트',
      editable: true,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.use_point}
        </Typography>
      )
    },
    {
      minWidth: 140,
      field: 'state',
      hideable: false,
      headerName: '상태*',
      renderCell: params => {  
        return (
          <Select
            value={params.row.state}
            onChange={(e) => {
              const newState = e.target.value;
              setList(list.map(item => {
                if (item.id === params.row.id) {
                  item.state = newState;
                }
                return item;
              }));
            }}
            size="small"
            native
          >
            <option value={1}>신청</option>
            <option value={2}>승인</option>
          </Select>
        )
      }
    },
    {
      flex: 0.1,
      field: 'uptime',
      minWidth: 50,
      headerName: '변경날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {new Date(params.row.uptime).toLocaleString('ko-KR')}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader 
        title='신청내역' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.id}
        pagination
        rows={list}
        rowCount={total}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: GiftshopUseToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            reload,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.id),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
        onCellEditStop={(params, event) => {
          if (params.reason === GridCellEditStopReasons.enterKeyDown) {        
            if (event.target.value?.length === 0) {
              return
            }

            // change source data
            setList(list.filter(item => {
              if (item.id === params.id) {
                item[params.field] = event.target.value
              }

              return item
            }));
          }
        }}
      />
    </Card>
  )
}

export default TableGiftUse